import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom"; 
import { useCart } from "../context/CartContext"; 
import { useTheme } from "../context/ThemeContext"; 
import { 
  Sparkles, Camera, ShoppingBag, Loader2, Info, 
  Check, X, RefreshCcw, ArrowRight, ScanFace, 
  ChevronRight, Layers, ShieldCheck
} from "lucide-react";
import SEO from '../components/SEO';

const AIStylist = () => {
  const [userImage, setUserImage] = useState(localStorage.getItem("ai_image") || null);
  const [preview, setPreview] = useState(localStorage.getItem("ai_image") || null);
  const [results, setResults] = useState(JSON.parse(localStorage.getItem("ai_results")) || []);
  const [selectedMood, setSelectedMood] = useState(localStorage.getItem("ai_mood") || "casual");
  const [step, setStep] = useState(parseInt(localStorage.getItem("ai_step")) || 1);
  const [loading, setLoading] = useState(false);
  
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const { addToCart } = useCart(); 
  const { isDarkMode } = useTheme();

  useEffect(() => {
    localStorage.setItem("ai_image", userImage || "");
    localStorage.setItem("ai_results", JSON.stringify(results));
    localStorage.setItem("ai_mood", selectedMood);
    localStorage.setItem("ai_step", step.toString());
  }, [userImage, results, selectedMood, step]);

  const moods = [
    { id: "casual", label: "Casual", icon: "👕" },
    { id: "formal", label: "Formal", icon: "👔" },
    { id: "party", label: "Party", icon: "🎉" },
    { id: "street", label: "Street", icon: "🛹" },
  ];

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        setUserImage(reader.result);
        setPreview(reader.result);
        setStep(2);
      };
    }
  };

  const getRecommendations = async () => {
    setLoading(true);
    try {
      const res = await axios.post("https://wethrift-backend.onrender.com/api/product/ai-stylist", 
        { userImage, mood: selectedMood },
        { headers: { "Content-Type": "application/json" } }
      );
      if (res.data.success) {
        setResults(res.data.data);
        setStep(3);
      }
    } catch (err) {
      alert("AI Service Offline. Please try again later.");
    } finally {
      setTimeout(() => setLoading(false), 2000); // Analysis feel
    }
  };

  const resetStylist = () => {
    localStorage.clear();
    setUserImage(null);
    setPreview(null);
    setResults([]);
    setStep(1);
  };

  return (
    <>
    
    <SEO 
  title={step === 3 ? "Your AI Style Curation" : "AI Virtual Stylist | Personalized Fashion"} 
  description={
    step === 3 
    ? `We've analyzed your face structure for the ${selectedMood} mood. Check out your personalized archive recommendations.` 
    : "Upload your portrait and let our Neural Engine suggest the perfect thrifted outfits based on your face structure and vibe."
  }
  url="/ai-stylist"
  image={preview || "/ai-stylist-og.jpg"} // Agar image upload ho gayi hai toh wahi preview SEO mein dikhega
/>

    <div className={`min-h-screen transition-all duration-500 ${isDarkMode ? 'bg-[#050505] text-white' : 'bg-[#fcfcfc] text-black'}`}>
      
      {/* --- MINIMAL HEADER --- */}
      <section className="pt-24 pb-10 px-6 text-center max-w-4xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-500 text-[10px] font-black uppercase tracking-widest mb-4 border border-emerald-500/20">
          <Layers size={12} /> Personalized Style Engine
        </div>
        <h1 className="text-4xl md:text-6xl font-black italic uppercase tracking-tighter leading-tight">
          Virtual <span className="text-emerald-500">Avatar</span> Studio
        </h1>
        <p className="mt-4 text-zinc-500 text-xs md:text-sm font-medium max-w-lg mx-auto leading-relaxed">
          Apni photo upload karein aur AI aapke face structure ke hisab se <span className="text-emerald-500">best outfits</span> suggest karega. Ekdum unique try-on experience.
        </p>
      </section>

      {/* --- PROGRESS STEPPER --- */}
      <div className="max-w-md mx-auto mb-16 px-6">
        <div className="relative flex justify-between items-center">
          {/* Progress Line Background */}
          <div className="absolute h-[2px] w-full bg-zinc-800 -z-10" />
          {/* Active Progress Line */}
          <div 
            className="absolute h-[2px] bg-emerald-500 transition-all duration-700 ease-in-out -z-10" 
            style={{ width: `${(step - 1) * 50}%` }}
          />
          
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex flex-col items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all duration-500 ${
                step >= s ? 'bg-emerald-500 border-emerald-500 text-black scale-110' : 'bg-zinc-900 border-zinc-700 text-zinc-500'
              }`}>
                {step > s ? <Check size={14} strokeWidth={4} /> : <span className="text-[10px] font-black">{s}</span>}
              </div>
              <span className={`text-[8px] font-black uppercase tracking-widest ${step >= s ? 'text-emerald-500' : 'text-zinc-600'}`}>
                {s === 1 ? 'Upload' : s === 2 ? 'Category' : 'Result'}
              </span>
            </div>
          ))}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 md:px-10 pb-24">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          
          {/* --- LEFT: UPLOAD & CONTROL --- */}
          <div className="lg:col-span-4 lg:sticky lg:top-28">
            <div className={`p-6 md:p-8 rounded-[2.5rem] border ${isDarkMode ? 'bg-zinc-900/40 border-white/5 shadow-2xl' : 'bg-white border-zinc-200 shadow-xl'}`}>
              
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-[10px] font-black uppercase text-zinc-500 tracking-widest">Workspace 01</h3>
                {step > 1 && <button onClick={resetStylist} className="text-zinc-500 hover:text-red-500"><RefreshCcw size={14} /></button>}
              </div>

              {step === 1 ? (
                <div 
                  onClick={() => fileInputRef.current.click()}
                  className="aspect-[4/5] border-2 border-dashed border-zinc-800 rounded-[2rem] flex flex-col items-center justify-center cursor-pointer group hover:border-emerald-500 transition-all"
                >
                  <div className="p-5 bg-emerald-500/10 rounded-full text-emerald-500 group-hover:scale-110 transition-transform">
                    <Camera size={28} />
                  </div>
                  <p className="mt-4 text-[10px] font-black uppercase text-zinc-500">Add Portrait Photo</p>
                  <input type="file" ref={fileInputRef} hidden onChange={handleImageChange} accept="image/*" />
                </div>
              ) : (
                <div className="space-y-6 animate-in fade-in duration-700">
                  <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden border border-white/5">
                    <img src={preview} className="w-full h-full object-cover" alt="User" />
                    {loading && (
                      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center">
                        <div className="w-full h-1 bg-emerald-500 absolute top-0 shadow-[0_0_20px_#10b981] animate-scan" />
                        <Loader2 className="animate-spin text-emerald-500 mb-3" size={32} />
                        <p className="text-[10px] font-black uppercase text-emerald-500">Analyzing Face...</p>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-4 gap-2">
                    {moods.map((m) => (
                      <button 
                        key={m.id} 
                        onClick={() => setSelectedMood(m.id)}
                        className={`py-3 rounded-xl flex flex-col items-center gap-1 transition-all ${
                          selectedMood === m.id ? 'bg-emerald-500 text-black' : 'bg-zinc-800/40 text-zinc-500 border border-white/5'
                        }`}
                      >
                        <span className="text-lg">{m.icon}</span>
                      </button>
                    ))}
                  </div>

                  {step === 2 && !loading && (
                    <button 
                      onClick={getRecommendations}
                      className="w-full py-4 bg-white text-black rounded-2xl font-black uppercase text-[10px] tracking-[0.2em] hover:bg-emerald-500 transition-all active:scale-95"
                    >
                      Process Recommendations
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* --- RIGHT: RESULTS --- */}
          <div className="lg:col-span-8">
            {step === 3 && results.length > 0 ? (
              <div className="space-y-6 animate-in slide-in-from-bottom-10 duration-1000">
                <div className="flex items-center gap-4 mb-8">
                  <div className="h-[1px] flex-1 bg-zinc-800" />
                  <span className="text-[10px] font-black uppercase text-zinc-500 tracking-[0.3em]">AI Selected Archive</span>
                  <div className="h-[1px] flex-1 bg-zinc-800" />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {results.map((item, idx) => (
                    <div key={idx} className={`p-5 rounded-[2.5rem] border transition-all hover:scale-[1.02] ${isDarkMode ? 'bg-zinc-900/20 border-white/5' : 'bg-white border-zinc-200 shadow-lg'}`}>
                      <div className="relative aspect-[3/4] rounded-[1.8rem] overflow-hidden mb-5">
                        <img src={item.productDetails.img} className="w-full h-full object-cover" alt="product" />
                        <div className="absolute top-4 left-4 px-3 py-1 bg-emerald-500 text-black text-[8px] font-black uppercase rounded-full">
                          Avatar Match: 99.8%
                        </div>
                      </div>
                      
                      <div className="px-2 space-y-4">
                        <div className="flex justify-between items-start">
                          <h4 className="text-xl font-black uppercase italic tracking-tighter max-w-[70%]">{item.outfitName}</h4>
                          <span className="text-emerald-500 font-bold">₹{item.productDetails.price}</span>
                        </div>
                        <p className="text-[11px] text-zinc-500 leading-relaxed line-clamp-2 italic">"{item.styleAdvice}"</p>
                        
                        <div className="flex gap-2">
                          <button 
                            onClick={() => navigate(`/product/${item.productDetails._id}`)}
                            className="flex-1 py-3 border border-zinc-800 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-zinc-800"
                          >
                            View Specs
                          </button>
                          <button 
                            onClick={() => addToCart(item.productDetails, "M")}
                            className="flex-[1.5] py-3 bg-white text-black rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-emerald-500 transition-all"
                          >
                            Add to Bag
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-[500px] flex flex-col items-center justify-center border-2 border-dashed border-zinc-900 rounded-[4rem] opacity-30">
                <ScanFace size={80} strokeWidth={0.5} />
                <p className="mt-4 text-[10px] font-black uppercase tracking-[0.4em]">Waiting for System Input</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <style>{`
        @keyframes scan {
          0% { top: 0%; }
          100% { top: 100%; }
        }
        .animate-scan {
          position: absolute;
          animation: scan 2s linear infinite;
        }
      `}</style>
    </div>
    
    </>
  );
};

export default AIStylist;